/**
 * Campaign System - Utility Functions
 * Funções puras para gerenciamento de campanhas
 */

import type {
  Contador,
  RecurrenceMode,
  TimeRemaining,
  ThemeClasses,
  OfertaThemeClasses,
} from './types';
import { supabase, isSupabaseConnected } from '../../lib/supabase';

const DEFAULT_STORAGE_KEY = 'campaign_contadores';

let cacheContadores: Contador[] | null = null;
let cacheContadoresPromise: Promise<Contador[]> | null = null;

function invalidateContadoresCache() {
  cacheContadores = null;
  cacheContadoresPromise = null;
}

/**
 * Carrega contadores do Supabase
 */
export async function getContadores(
  storageKey: string = DEFAULT_STORAGE_KEY
): Promise<Contador[]> {
  if (cacheContadores) {
    return cacheContadores;
  }

  if (cacheContadoresPromise) {
    return cacheContadoresPromise;
  }

  if (!isSupabaseConnected()) {
    console.warn('Supabase não conectado, usando localStorage como fallback');
    return getContadoresFromLocalStorage(storageKey);
  }

  cacheContadoresPromise = (async () => {
    try {
      const { data, error } = await supabase
        .from('ibx_contadores')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const mapped: Contador[] = (data || []).map((row) => ({
        id: row.id,
        titulo: row.titulo,
        texto: row.texto ?? undefined,
        dataFim: new Date(row.data_fim),
        location: row.location,
        tema: row.tema as 'verde' | 'laranja' | 'petrol',
        modoRecorrencia: mapRecurrenceFromDB(row.modo_recorrencia),
        ativo: row.ativo,
        textoBotao: row.texto_botao ?? undefined,
        urlBotao: row.url_botao ?? undefined,
        precoOriginal: row.preco_original ?? undefined,
        precoAtual: row.preco_atual ?? undefined,
        beneficios: Array.isArray(row.beneficios) ? row.beneficios : undefined,
      }));

      cacheContadores = mapped;
      return mapped;
    } catch (error) {
      console.error('Erro ao carregar contadores do Supabase:', error);
      return getContadoresFromLocalStorage(storageKey);
    } finally {
      cacheContadoresPromise = null;
    }
  })();

  return cacheContadoresPromise;
}

/**
 * Salva contador no Supabase (insert ou update)
 */
export async function saveContador(contador: Contador): Promise<boolean> {
  if (!isSupabaseConnected()) {
    console.warn('Supabase não conectado');
    return false;
  }

  try {
    const dbData = {
      id: contador.id.startsWith('lead_') ? undefined : contador.id,
      titulo: contador.titulo,
      texto: contador.texto ?? null,
      data_fim: contador.dataFim.toISOString(),
      location: contador.location,
      tema: contador.tema,
      modo_recorrencia: mapRecurrenceToDB(contador.modoRecorrencia),
      ativo: contador.ativo,
      texto_botao: contador.textoBotao ?? null,
      url_botao: contador.urlBotao ?? null,
      preco_original: contador.precoOriginal ?? null,
      preco_atual: contador.precoAtual ?? null,
      beneficios: contador.beneficios ?? null,
    };

    const { error } = await supabase
      .from('ibx_contadores')
      .upsert(dbData, { onConflict: 'id' });

    if (error) throw error;

    invalidateContadoresCache();
    return true;
  } catch (error) {
    console.error('Erro ao salvar contador no Supabase:', error);
    return false;
  }
}

/**
 * Atualiza múltiplos contadores no Supabase
 */
export async function saveContadores(
  contadores: Contador[],
  storageKey: string = DEFAULT_STORAGE_KEY
): Promise<void> {
  if (!isSupabaseConnected()) {
    console.warn('Supabase não conectado, usando localStorage como fallback');
    saveContadoresToLocalStorage(contadores, storageKey);
    invalidateContadoresCache();
    return;
  }

  try {
    const promises = contadores.map((contador) => saveContador(contador));
    await Promise.all(promises);
    invalidateContadoresCache();
  } catch (error) {
    console.error('Erro ao salvar contadores:', error);
    saveContadoresToLocalStorage(contadores, storageKey);
    invalidateContadoresCache();
  }
}

/**
 * Deleta contador do Supabase
 */
export async function deleteContador(id: string): Promise<boolean> {
  if (!isSupabaseConnected()) {
    console.warn('Supabase não conectado');
    return false;
  }

  try {
    const { error } = await supabase
      .from('ibx_contadores')
      .delete()
      .eq('id', id);

    if (error) throw error;

    invalidateContadoresCache();
    return true;
  } catch (error) {
    console.error('Erro ao deletar contador do Supabase:', error);
    return false;
  }
}

/**
 * Atualiza apenas o campo 'ativo' de um contador
 */
export async function toggleContadorAtivo(
  id: string,
  ativo: boolean
): Promise<boolean> {
  if (!isSupabaseConnected()) {
    console.warn('Supabase não conectado');
    return false;
  }

  try {
    const { error } = await supabase
      .from('ibx_contadores')
      .update({ ativo })
      .eq('id', id);

    if (error) throw error;

    invalidateContadoresCache();
    return true;
  } catch (error) {
    console.error('Erro ao atualizar status do contador:', error);
    return false;
  }
}

// ============================================
// FUNÇÕES DE FALLBACK (localStorage)
// ============================================

function getContadoresFromLocalStorage(storageKey: string): Contador[] {
  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) return [];

    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];

    return parsed.map((item) => ({
      ...item,
      dataFim: new Date(item.dataFim),
    }));
  } catch {
    return [];
  }
}

function saveContadoresToLocalStorage(
  contadores: Contador[],
  storageKey: string
): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(contadores));
  } catch (error) {
    console.error('Erro ao salvar contadores no localStorage:', error);
  }
}

// ============================================
// MAPEAMENTO DE DADOS
// ============================================

function mapRecurrenceFromDB(dbValue: string): RecurrenceMode {
  const map: Record<string, RecurrenceMode> = {
    unico: 'sem-recorrencia',
    diario: 'semanal',
    semanal: 'semanal',
    mensal: 'mensal',
  };
  return map[dbValue] || 'sem-recorrencia';
}

function mapRecurrenceToDB(appValue: RecurrenceMode): string {
  const map: Record<RecurrenceMode, string> = {
    'sem-recorrencia': 'unico',
    semanal: 'semanal',
    mensal: 'mensal',
  };
  return map[appValue] || 'unico';
}

// ============================================
// FUNÇÕES UTILITÁRIAS
// ============================================

/**
 * Calcula a próxima data de recorrência
 */
export function calculateNextRecurrence(
  originalDate: string,
  mode: RecurrenceMode
): string {
  if (mode === 'sem-recorrencia') return originalDate;

  const original = new Date(originalDate);
  const now = new Date();

  if (mode === 'semanal') {
    const nextDate = new Date(original);
    while (nextDate <= now) {
      nextDate.setDate(nextDate.getDate() + 7);
    }
    return nextDate.toISOString();
  }

  if (mode === 'mensal') {
    const nextDate = new Date(original);
    while (nextDate <= now) {
      nextDate.setMonth(nextDate.getMonth() + 1);
    }
    return nextDate.toISOString();
  }

  return originalDate;
}

/**
 * Calcula tempo restante até a data alvo
 */
export function getTimeRemaining(
  targetDate: string,
  mode: RecurrenceMode
): TimeRemaining {
  const effectiveTarget = calculateNextRecurrence(targetDate, mode);
  const now = new Date().getTime();
  const target = new Date(effectiveTarget).getTime();
  const diff = target - now;

  if (diff <= 0 && mode === 'sem-recorrencia') {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  }

  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: false };
  }

  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000),
    expired: false,
  };
}

/**
 * Calcula tempo restante (formato alternativo para TopBar e SecaoOferta)
 */
export function calculateTimeLeft(
  dataFim: string,
  recorrencia?: 'nenhuma' | 'semanal' | 'mensal'
): { dias: number; horas: number; minutos: number; segundos: number } | null {
  let targetDate = new Date(dataFim);

  if (recorrencia === 'semanal') {
    const now = new Date();
    const dayOfWeek = targetDate.getDay();
    const currentDayOfWeek = now.getDay();
    const daysUntilTarget = (dayOfWeek - currentDayOfWeek + 7) % 7;

    targetDate = new Date(now);
    targetDate.setDate(now.getDate() + daysUntilTarget);
    targetDate.setHours(new Date(dataFim).getHours());
    targetDate.setMinutes(new Date(dataFim).getMinutes());
    targetDate.setSeconds(0);

    if (targetDate <= now) {
      targetDate.setDate(targetDate.getDate() + 7);
    }
  } else if (recorrencia === 'mensal') {
    const now = new Date();
    const targetDay = targetDate.getDate();

    targetDate = new Date(now.getFullYear(), now.getMonth(), targetDay);
    targetDate.setHours(new Date(dataFim).getHours());
    targetDate.setMinutes(new Date(dataFim).getMinutes());
    targetDate.setSeconds(0);

    if (targetDate <= now) {
      targetDate.setMonth(targetDate.getMonth() + 1);
    }
  }

  const difference = targetDate.getTime() - new Date().getTime();

  if (difference <= 0) {
    return null;
  }

  return {
    dias: Math.floor(difference / (1000 * 60 * 60 * 24)),
    horas: Math.floor((difference / (1000 * 60 * 60)) % 24),
    minutos: Math.floor((difference / 1000 / 60) % 60),
    segundos: Math.floor((difference / 1000) % 60),
  };
}

/**
 * Retorna classes CSS para o tema do contador
 */
export function getTemaClasses(
  tema: 'verde' | 'laranja' | 'petrol'
): ThemeClasses {
  const themes: Record<string, ThemeClasses> = {
    verde: {
      container: 'bg-green-50 border-green-300',
      block: 'bg-white text-green-700 border-green-200',
      text: 'text-green-700',
    },
    laranja: {
      container: 'bg-orange-50 border-orange-300',
      block: 'bg-white text-orange-700 border-orange-200',
      text: 'text-orange-700',
    },
    petrol: {
      container: 'bg-[#0F3D3E]/10 border-[#0F3D3E]/30',
      block: 'bg-white text-[#0F3D3E] border-[#0F3D3E]/20',
      text: 'text-[#0F3D3E]',
    },
  };
  return themes[tema];
}

/**
 * Retorna classes CSS para TopBar
 */
export function getTopBarTemaClasses(
  tema: 'verde' | 'laranja' | 'petrol'
): string {
  const themes: Record<string, string> = {
    verde: 'bg-gradient-to-r from-green-600 to-green-700',
    laranja: 'bg-gradient-to-r from-orange-600 to-orange-700',
    petrol: 'bg-gradient-to-r from-teal-600 to-teal-700',
  };
  return themes[tema];
}

/**
 * Retorna classes CSS para SecaoOferta
 */
export function getOfertaTemaClasses(
  tema: 'verde' | 'laranja' | 'petrol'
): OfertaThemeClasses {
  const themes: Record<string, OfertaThemeClasses> = {
    verde: {
      bg: 'bg-gradient-to-br from-green-50 to-green-100',
      border: 'border-green-200',
      text: 'text-green-700',
      button: 'bg-green-600 hover:bg-green-700',
      timer: 'bg-green-600',
    },
    laranja: {
      bg: 'bg-gradient-to-br from-orange-50 to-orange-100',
      border: 'border-orange-200',
      text: 'text-orange-700',
      button: 'bg-orange-600 hover:bg-orange-700',
      timer: 'bg-orange-600',
    },
    petrol: {
      bg: 'bg-gradient-to-br from-teal-50 to-teal-100',
      border: 'border-teal-200',
      text: 'text-teal-700',
      button: 'bg-teal-600 hover:bg-teal-700',
      timer: 'bg-teal-600',
    },
  };
  return themes[tema];
}

/**
 * Formata preço para parcelas
 */
export function formatarParcelas(
  precoAtual: string,
  parcelas: number = 12
): string {
  try {
    const valor = parseFloat(
      precoAtual.replace(/[^\d,]/g, '').replace(',', '.')
    );
    const valorParcela = valor / parcelas;
    return valorParcela.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  } catch {
    return 'R$ 0,00';
  }
}